import React, { useState, useEffect, useCallback } from "react";
import MatchCard from "./MatchCard";
import "./Partidos.css";
import { supabase } from "../supabaseClient";
import {
  formatGameDate,
  formatGameTime,
  localISODate,
} from "../Functions/Formatting";
import { matchStatus, sortRounds } from "../Functions/matchStatus";
import { DayGroup, MatchListSkeleton } from "./MatchListParts";
import StateMessage from "./StateMessage";

/**
 * Round to open first: the earliest round (in tournament order) that still
 * has a game today or later; once everything is past, the last round.
 */
function pickCurrentRoundIndex(orderedRounds, lastDateByRound) {
  const today = localISODate();
  const idx = orderedRounds.findIndex(
    (r) => (lastDateByRound[r] || "") >= today
  );
  return idx === -1 ? orderedRounds.length - 1 : idx;
}
const FRESH_MS = 30_000;
const PREFETCH_DELAY_MS = 300;

const PartidosFinal = ({ leagueFilter, themeColor }) => {
  const [roundsCache, setRoundsCache] = useState({});
  const [gamesCache, setGamesCache] = useState({});
  const [rounds, setRounds] = useState([]);
  const [currentRoundIndex, setCurrentRoundIndex] = useState(0);
  const [error, setError] = useState(null);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [periodLoading, setPeriodLoading] = useState(false);

  const currentRound = rounds[currentRoundIndex];

  const fetchSupabaseGames = async (roundName) => {
    const { data: rows, error } = await supabase
      .from('games')
      .select('game_id:id, league_id, team1_id, team2_id, team1_score, team2_score, game_date, game_time, status, t1:teams!team1_id(team_name, logo_url, primary_kit_colors), t2:teams!team2_id(team_name, logo_url, primary_kit_colors), leagues!inner(id)')
      .eq('league_id', leagueFilter)
      .eq('game_type', 'FINALS')
      .eq('round', roundName)
      .eq('is_published', true)
      .eq('leagues.is_published', true)
      .order('game_date', { ascending: true })
      .order('game_time', { ascending: true });
    
    if (error) throw error;
    
    return (rows || []).map(row => ({
      ...row,
      team1_name: row.t1?.team_name,
      team2_name: row.t2?.team_name,
      team1_logo_url: row.t1?.logo_url || null,
      team2_logo_url: row.t2?.logo_url || null,
      team1_kit_colors: row.t1?.primary_kit_colors || [],
      team2_kit_colors: row.t2?.primary_kit_colors || [],
    }));
  };

  const prefetchRound = useCallback(
    async (roundName) => {
      if (!leagueFilter || !roundName) return;
      const cacheKey = `${leagueFilter}:FINALS:${roundName}`;
      const cached = gamesCache[cacheKey];
      const now = Date.now();
      if (cached && now - cached.ts < FRESH_MS) return;

      try {
        const data = await fetchSupabaseGames(roundName);
        setGamesCache((prev) => ({
          ...prev,
          [cacheKey]: { data, ts: Date.now() },
        }));
      } catch {
        // silent prefetch
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gamesCache, leagueFilter]
  );

  useEffect(() => {
    if (!leagueFilter) return;

    const cacheKey = `${leagueFilter}:FINALS:ROUNDS`;
    const cached = roundsCache[cacheKey];
    const now = Date.now();

    if (cached?.data) {
      setRounds(cached.data);
      if (cached.data.length > 0) {
        setCurrentRoundIndex(
          Math.min(Math.max(cached.currentIndex ?? 0, 0), cached.data.length - 1)
        );
      }
      setLoadingInitial(false);
    } else {
      setLoadingInitial(true);
    }

    if (cached && now - cached.ts < FRESH_MS) return;

    let isMounted = true;
    (async () => {
      try {
        const { data: rows, error } = await supabase
          .from('games')
          .select('round, game_date, leagues!inner(id)')
          .eq('league_id', leagueFilter)
          .eq('game_type', 'FINALS')
          .eq('is_published', true)
          .eq('leagues.is_published', true);

        if (error) throw error;
        
        const lastDateByRound = {};
        for (const r of rows || []) {
          const d = String(r.game_date || "").slice(0, 10);
          if (!lastDateByRound[r.round] || d > lastDateByRound[r.round]) {
            lastDateByRound[r.round] = d;
          }
        }
        const data = sortRounds([...new Set((rows || []).map(r => r.round))]);
        const currentIndex = pickCurrentRoundIndex(data, lastDateByRound);

        if (isMounted) {
          setRoundsCache((prev) => ({
            ...prev,
            [cacheKey]: { data, currentIndex, ts: Date.now() },
          }));
          setRounds(data);

          if (data.length > 0) {
            setCurrentRoundIndex(currentIndex);
          }
          setLoadingInitial(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Error fetching finals rounds:", err);
          if (!cached) setError("No se pudieron cargar las finales.");
          setLoadingInitial(false);
        }
      }
    })();

    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueFilter]);

  useEffect(() => {
    if (!leagueFilter || !currentRound) return;

    const cacheKey = `${leagueFilter}:FINALS:${currentRound}`;
    const cached = gamesCache[cacheKey];
    const now = Date.now();

    setPeriodLoading(!cached?.data);
    let isMounted = true;

    if (cached && now - cached.ts < FRESH_MS) {
      setPeriodLoading(false);
    } else {
      (async () => {
        try {
          const data = await fetchSupabaseGames(currentRound);
          if (isMounted) {
            setGamesCache((prev) => ({
              ...prev,
              [cacheKey]: { data, ts: Date.now() },
            }));
          }
        } catch (err) {
          if (isMounted) {
            console.error("Error loading games data:", err);
            if (!cached) setError("No se pudieron cargar los partidos de esta ronda.");
          }
        } finally {
          if (isMounted) setPeriodLoading(false);
        }
      })();
    }
    
    return () => { isMounted = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueFilter, currentRound, gamesCache]);

  useEffect(() => {
    if (!rounds.length || currentRoundIndex == null) return;
    const prevIdx = currentRoundIndex - 1;
    const nextIdx = currentRoundIndex + 1;
    if (prevIdx >= 0) prefetchRound(rounds[prevIdx]);
    if (nextIdx < rounds.length) prefetchRound(rounds[nextIdx]);
  }, [rounds, currentRoundIndex, prefetchRound]);

  useEffect(() => {
    if (!rounds.length || currentRoundIndex == null) return;
    const id = setTimeout(() => {
      const minus2 = currentRoundIndex - 2;
      const plus2 = currentRoundIndex + 2;
      if (minus2 >= 0) prefetchRound(rounds[minus2]);
      if (plus2 < rounds.length) prefetchRound(rounds[plus2]);
    }, PREFETCH_DELAY_MS);
    return () => clearTimeout(id);
  }, [rounds, currentRoundIndex, prefetchRound]);

  const gamesForCurrentRound =
    gamesCache[`${leagueFilter}:FINALS:${currentRound}`]?.data || [];

  const groupedGames = gamesForCurrentRound.reduce((acc, game) => {
    const dateStr = game.game_date;
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(game);
    return acc;
  }, {});
  const dates = Object.keys(groupedGames).sort();
  const roundLoading = periodLoading && gamesForCurrentRound.length === 0;

  return (
    <section className="finals-block matchcard-container" aria-labelledby="finals-title">
      <header className="list-block-head">
        <span className="block-eyebrow">Liguilla</span>
        <h2 className="block-title" id="finals-title">Finales</h2>
      </header>

      {loadingInitial ? (
        <MatchListSkeleton rows={1} />
      ) : error ? (
        <StateMessage kind="error" title={error}>
          Intenta recargar la página.
        </StateMessage>
      ) : rounds.length === 0 ? (
        <StateMessage title="Rondas por publicarse">
          Los partidos de finales aparecerán aquí cuando se publiquen.
        </StateMessage>
      ) : (
        <>
          <div className="round-tabs" role="group" aria-label="Rondas de finales">
            {rounds.map((round, idx) => (
              <button
                key={round}
                type="button"
                className={`round-tab${idx === currentRoundIndex ? " active" : ""}`}
                aria-pressed={idx === currentRoundIndex}
                onClick={() => setCurrentRoundIndex(idx)}
              >
                {round}
              </button>
            ))}
          </div>

          {periodLoading && gamesForCurrentRound.length > 0 && (
            <div className="subtle-badge" role="status">Actualizando…</div>
          )}

          {roundLoading ? (
            <MatchListSkeleton rows={1} />
          ) : gamesForCurrentRound.length > 0 ? (
            dates.map((dateStr) => (
              <DayGroup key={dateStr} dateStr={dateStr}>
                {groupedGames[dateStr].map((game) => (
                  <MatchCard
                    key={game.game_id}
                    gameID={game.game_id}
                    team1={game.team1_name}
                    team1Logo={game.team1_logo_url}
                    team1KitColors={game.team1_kit_colors}
                    team2={game.team2_name}
                    team2Logo={game.team2_logo_url}
                    team2KitColors={game.team2_kit_colors}
                    time={formatGameTime(game.game_time)}
                    date={formatGameDate(game.game_date)}
                    status={matchStatus(game)}
                    team1Score={game.team1_score}
                    team2Score={game.team2_score}
                    league={game.league_id}
                    displayLeague={false}
                    roundLabel={currentRound}
                    themeColor={themeColor}
                  />
                ))}
              </DayGroup>
            ))
          ) : (
            <StateMessage plain>No hay partidos publicados en esta ronda.</StateMessage>
          )}
        </>
      )}
    </section>
  );
};

export default PartidosFinal;
